import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../lib/api';
import { getFromDB, setInDB, quickHash } from '../lib/dataCache';

/**
 * Smart API query hook with 2-layer caching:
 * Layer 1: React Query (in-memory) — instant on navigation
 * Layer 2: IndexedDB (persistent) — instant on page reload
 *
 * Flow: IndexedDB → show instantly → fetch API in background → update if changed
 */
export function useApiQuery(key, url, options = {}) {
  const queryKey = Array.isArray(key) ? key : [key];
  const cacheKey = queryKey.join('_');
  const staleTime = options.staleTime || 5 * 60 * 1000;

  return useQuery({
    queryKey,
    queryFn: async () => {
      // Always fetch fresh from API. IndexedDB se koristi SAMO ako API padne (fallback).
      // Ne smije se vraćati stari IndexedDB data kao success — to je uzrok "stuck na starom".
      try {
        const { data } = await api.get(url);
        // Save to IndexedDB in background (don't block); only the latest version remains.
        setInDB(cacheKey, data, quickHash(data)).catch(() => {});
        return data;
      } catch (err) {
        // API failure → koristi IndexedDB fallback samo ako je svjež (manje od 30 min)
        try {
          const cached = await getFromDB(cacheKey);
          if (cached && (Date.now() - (cached.timestamp || 0) < 30 * 60 * 1000)) {
            return cached.data;
          }
        } catch (_) { /* ignore */ }
        throw err;
      }
    },
    initialData: undefined,
    placeholderData: undefined,
    staleTime,
    // 14.09.2026: restart backenda (~30–60 s, 502) je ostavljao CRM bez zaduženja ("obrisala su se").
    // Pokušaj ponovno do ~1 min (1.5, 3, 6, 12, 20, 20 s) i dok je u grešci osvježavaj svakih 30 s.
    retry: 6,
    retryDelay: (n) => Math.min(1500 * 2 ** n, 20000),
    refetchInterval: (q) => ((q?.state?.status ?? q) === 'error' ? 30000 : false),
    ...options,
    keepPreviousData: true,
  });
}

/**
 * Initialize query cache from IndexedDB on app start.
 * Call this once after login to pre-populate React Query cache.
 */
export async function hydrateFromIndexedDB(queryClient) {
  // SAMO za key-eve koji se rijetko mijenjaju. NE hydrate dashboard/leads
  // jer cijene su jako dinamične (backfill, novi enrichment, etc.) i staro
  // hydratiranje uzrokuje "stuck na 0" bug — IndexedDB pre-populira React Query
  // s starim response-om prije nego što stigne svježi fetch.
  const keys = [
    { queryKey: ['company-status'], cacheKey: 'company-status' },
    { queryKey: ['watchlist'], cacheKey: 'watchlist' },
    { queryKey: ['reminders'], cacheKey: 'reminders' },
    { queryKey: ['crm-companies'], cacheKey: 'crm-companies' },
  ];

  await Promise.allSettled(
    keys.map(async ({ queryKey, cacheKey }) => {
      const cached = await getFromDB(cacheKey);
      // Skraćeni TTL: 5 min (umjesto 30) — dovoljno za page reload, ne za stale data
      if (cached && (Date.now() - cached.timestamp < 5 * 60 * 1000)) {
        queryClient.setQueryData(queryKey, cached.data);
      }
    })
  );
}

// For mutations that invalidate cache
export function useApiMutation(url, method = 'post', invalidateKeys = []) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body) => {
      const { data } = await api[method](url, body);
      return data;
    },
    onSuccess: () => {
      invalidateKeys.forEach(key => queryClient.invalidateQueries({ queryKey: [key] }));
    },
  });
}

// Cache times by data type
export const CACHE_TIMES = {
  dashboard: { staleTime: 2 * 60 * 1000 },      // 2 min
  leads: { staleTime: 5 * 60 * 1000 },           // 5 min
  statistics: { staleTime: 15 * 60 * 1000 },     // 15 min
  history: { staleTime: 30 * 60 * 1000 },        // 30 min
  users: { staleTime: 10 * 60 * 1000 },          // 10 min
  crm: { staleTime: 3 * 60 * 1000 },             // 3 min
};
